/**
 * grainSize.ts — de grondsoort van een laag afleiden uit zijn korrelverdeling.
 *
 * Een grondsoort is geen keuze maar een uitkomst: hoeveel lutum, silt, zand, grind en
 * organische stof er in het monster zit, bepaalt hoe de laag heet. Wie die percentages heeft --
 * uit het lab of geschat in het veld -- hoeft de naam niet meer zelf op te zoeken.
 *
 * Twee diagrammen doen het werk, en ze staan los van elkaar:
 *
 *  1. De **lutum-silt-zanddriehoek** (NEN 6693 fig. D.4, NEN 5104 heeft zijn eigen versie)
 *     geeft de minerale naam. Grind komt daar niet in voor; dat is een eigen driehoek (D.2)
 *     die erboven ligt: pas boven de helft grind is het een grindlaag, daaronder is grind een
 *     bijmenging.
 *  2. De **organische-stofdriehoek** (fig. D.3) komt daar bovenop en bepaalt of organische stof
 *     een bijmenging blijft (humeus) of het hoofdbestanddeel wordt (veen).
 *
 * EENHEDEN, en dit is de valkuil: lutum, silt, zand en grind zijn percentages van de MINERALE
 * delen; organische stof is een percentage van de TOTALE droge massa. Die twee tellen dus niet
 * samen op tot 100. In D.3 wordt lutum daarom eerst teruggerekend naar % van het totaal.
 *
 * GRENSREGEL (D.1): een waarde precies op een grens krijgt de naam van het GROFSTE veld.
 * Daarom staan de lutumgrenzen hier strikt (`>`) en de zand- en grindgrenzen inclusief (`>=`).
 *
 * Deze module rekent en bewaart niets. De uitkomst wordt door de aanroeper weggeschreven naar
 * de gewone laagvelden (soilType, toevoeging1..3, gradatie1..3, mediaan), zodat de export, de
 * boorprofieltekening en de zoekfunctie nergens iets van korrelverdelingen hoeven te weten.
 *
 * Herkomst van de regels: NEN 6693:2025 bijlage D (fig. D.2 grinddriehoek, D.3 organische stof,
 * D.4 lutum-silt-zand, tabel D.1), 8.3.1 (grindlaag boven 50 %) en de korrelgroottetabel voor
 * de mediaan. De NEN 5104-varianten staan erbij waar ze afwijken; de NEN 5104-grindklassen zijn
 * AFGELEID uit TerraIndex-uitvoer en niet uit de normtekst zelf.
 */

import { usesNen6693Codes, type SoilStandard } from './standards.js';
// Alleen voor het opzoeken van codes onderaan dit bestand; de rekenregels hierboven kennen
// geen codelijsten en zijn los te testen.
import * as NEN6693 from './codelists/nen6693.js';
import * as CODELISTS from './codelists/index.js';

// ─── Invoer en uitkomst ─────────────────────────────────────────────────────

export type GrainSizeInput = {
  /** Lutum, % van de minerale delen (< 2 µm). */
  lutumPct?: number | null;
  /** Leem = lutum + silt, % van de minerale delen. Alternatief voor een van die twee. */
  leemPct?: number | null;
  /** Grind, % van de minerale delen (> 2 mm). */
  grindPct?: number | null;
  /** Silt, % van de minerale delen (2 - 63 µm). */
  siltPct?: number | null;
  /** Organische stof, % van de TOTALE droge massa -- niet van de minerale delen. */
  organicPct?: number | null;
  /** Mediane korrelgrootte in µm. Bepaalt de mediaanklasse bij zand en grind. */
  m50Um?: number | null;
};

/** De vier minerale fracties, elk als % van de minerale delen. Tellen op tot 100. */
export type GrainFractions = { lutum: number; silt: number; zand: number; grind: number };

export type GrainAdmixture = {
  /** Term uit BIJMENGING / TOEVOEGING: siltig, zandig, kleiig, grindig, humeus. */
  admixture: string;
  /** Term uit INTENSITEIT / GRADATIE: zwak, matig, sterk, uiterst. Leeg = geen gradatie. */
  intensity: string;
};

export type GrainClassification = {
  /** Hoofdbestanddeel als term: zand, klei, silt (6693) of leem (5104), grind, veen. */
  soil: string;
  /** Bijmengingen in opslagvolgorde: textuur, dan grindig, dan humeus. */
  admixtures: GrainAdmixture[];
  /** Mediaanklasse-term, of null als er geen M50 is of hij niet van toepassing is. */
  median: string | null;
  /** Veen zonder minerale bijmenging (Vm uit fig. D.3) -> het bestaande veld `mineraalarm`. */
  mineraalarm: boolean;
  /** De minerale fracties waarop gerekend is, na het aanvullen van wat niet is opgegeven. */
  fractions: GrainFractions;
  /** Wat er onderweg aangenomen of genegeerd is. Bedoeld om te tonen, niet om te negeren. */
  notes: string[];
};

/** Kan deze invoer een grondsoort opleveren? Alles leeg = nee, en dat is geen fout. */
export function hasGrainSizeInput(input: GrainSizeInput | null | undefined): boolean {
  if (!input) return false;
  return [input.lutumPct, input.leemPct, input.grindPct, input.siltPct,
          input.organicPct, input.m50Um].some((v) => v != null && v !== ('' as unknown));
}

// ─── Stap 1: de vier minerale fracties bepalen ──────────────────────────────

const num = (v: number | null | undefined): number | null =>
  v == null || Number.isNaN(Number(v)) ? null : Number(v);

/**
 * Lutum, silt, zand en grind uit wat er is ingevuld.
 *
 * Leem is per definitie lutum + silt, dus van die drie zijn er twee genoeg. De volgorde
 * hieronder is de voorrang: staan lutum en silt er allebei, dan winnen die van leem -- een
 * leemwaarde die daar niet bij past is een invoerfout, geen derde bron.
 *
 * Staat ALLEEN leem er, dan is de verdeling tussen lutum en silt onbekend. We rekenen dan met
 * lutum = 0, oftewel: leem is helemaal silt. Niet omdat dat altijd zo is, maar omdat de
 * lutumkolom leeg laten de enige manier is om te zeggen 'geen lutum' -- en een lutumwaarde
 * verzinnen zou de grondsoort verschuiven zonder dat iemand dat gevraagd heeft. Het staat wel
 * in `notes`, zodat het scherm het kan tonen in plaats van het te verzwijgen.
 *
 * (TerraIndex lijkt hier lutum ~ 22,5 % van leem te nemen. Dat is een vermoeden uit hun
 * uitvoer, geen norm -- en het is precies het soort stille aanname dat wij niet doen.)
 */
export function resolveFractions(
  input: GrainSizeInput,
): { fractions: GrainFractions | null; notes: string[] } {
  const notes: string[] = [];
  const lutum = num(input.lutumPct);
  const silt = num(input.siltPct);
  const leem = num(input.leemPct);
  const grind = num(input.grindPct) ?? 0;

  let L: number;
  let S: number;
  if (lutum != null && silt != null) {
    L = lutum; S = silt;
    if (leem != null && Math.abs(leem - (L + S)) > 0.01) {
      notes.push(`Leem (${leem}%) komt niet overeen met lutum + silt (${L + S}%); lutum en silt zijn gebruikt.`);
    }
  } else if (lutum != null && leem != null) {
    L = lutum; S = leem - lutum;
  } else if (silt != null && leem != null) {
    L = leem - silt; S = silt;
  } else if (leem != null) {
    L = 0; S = leem;
    notes.push('Alleen leem opgegeven; gerekend zonder lutum. Vul lutum of silt in als de verdeling anders is.');
  } else {
    L = lutum ?? 0; S = silt ?? 0;
  }

  if (L < 0 || S < 0) {
    notes.push('Ongeldig: silt of lutum is groter dan leem.');
    return { fractions: null, notes };
  }
  const zand = 100 - grind - L - S;
  if (zand < -1e-9) {
    notes.push('Ongeldig: de minerale percentages tellen samen op tot meer dan 100.');
    return { fractions: null, notes };
  }
  return { fractions: { lutum: L, silt: S, zand: Math.max(zand, 0), grind }, notes };
}

/** Lutum, silt en zand herschaald naar 100 % van het GRINDVRIJE deel -- de assen van D.4. */
export function normaliseFine(f: GrainFractions): { lutum: number; silt: number; zand: number } {
  const rest = f.lutum + f.silt + f.zand;
  if (rest <= 0) return { lutum: 0, silt: 0, zand: 0 };
  return { lutum: (100 * f.lutum) / rest, silt: (100 * f.silt) / rest, zand: (100 * f.zand) / rest };
}

// ─── Stap 2: de lutum-silt-zanddriehoek (D.4) ───────────────────────────────

type LszCode =
  | 'Ks1' | 'Ks2' | 'Ks3' | 'Ks4' | 'Kz1' | 'Kz2' | 'Kz3'
  | 'Zk' | 'Zs1' | 'Zs2' | 'Zs3' | 'Zs4' | 'Sz1' | 'Sz3';

/** De dikke klei/silt-lijn in D.4: van (zand 50, lutum 8) naar (zand 0, lutum 25). */
const kleiSiltLijn = (zand: number): number => 8 + (17 * (50 - zand)) / 50;

/**
 * De code uit de lutum-silt-zanddriehoek. Invoer is genormaliseerd op het grindvrije deel.
 *
 * NEN 5104 legt de grens tussen zwak en matig siltig zand bij 92 % zand, NEN 6693 bij 90 %.
 * Dat is het enige verschil in deze driehoek; de rest van de lijnen valt samen.
 */
export function classifyLsz(
  lutum: number, silt: number, zand: number, standard: SoilStandard,
): LszCode {
  const zs1Grens = usesNen6693Codes(standard) ? 90 : 92;

  // Klei, van fijn naar grof -- lutumgrenzen strikt (grensregel D.1).
  if (lutum > 50) return 'Ks1';
  if (lutum > 35) return 'Ks2';
  if (lutum > 25) return 'Ks3';

  if (zand >= 50) {
    if (lutum > 8) {
      if (lutum > 17.5) return 'Kz1';
      if (lutum > 12) return 'Kz2';
      return 'Kz3';
    }
    // Zanddomein: lutum <= 8.
    if (lutum > 5 && zand >= 82.5) return 'Zk';
    if (zand >= zs1Grens) return 'Zs1';
    if (zand >= 82.5) return 'Zs2';
    if (zand >= 67.5) return 'Zs3';
    return 'Zs4';
  }

  // Minder dan de helft zand: klei boven de dikke lijn, silt eronder of erop.
  if (lutum > kleiSiltLijn(zand)) return 'Ks4';
  return zand >= 15 ? 'Sz3' : 'Sz1';
}

/** Code uit D.4 -> hoofdbestanddeel plus zijn textuurbijmenging. */
const LSZ_NAMEN: Record<LszCode, { soil: string; admixture: string; intensity: string }> = {
  Ks1: { soil: 'klei', admixture: 'siltig', intensity: 'zwak' },
  Ks2: { soil: 'klei', admixture: 'siltig', intensity: 'matig' },
  Ks3: { soil: 'klei', admixture: 'siltig', intensity: 'sterk' },
  Ks4: { soil: 'klei', admixture: 'siltig', intensity: 'uiterst' },
  Kz1: { soil: 'klei', admixture: 'zandig', intensity: 'zwak' },
  Kz2: { soil: 'klei', admixture: 'zandig', intensity: 'matig' },
  Kz3: { soil: 'klei', admixture: 'zandig', intensity: 'sterk' },
  // Zand met lutum tussen 5 en 8: 'kleiig' kent in de norm geen gradatie (Tabel 11/13).
  Zk: { soil: 'zand', admixture: 'kleiig', intensity: '' },
  Zs1: { soil: 'zand', admixture: 'siltig', intensity: 'zwak' },
  Zs2: { soil: 'zand', admixture: 'siltig', intensity: 'matig' },
  Zs3: { soil: 'zand', admixture: 'siltig', intensity: 'sterk' },
  Zs4: { soil: 'zand', admixture: 'siltig', intensity: 'uiterst' },
  // NEN 6693 kent 'silt' waar NEN 5104 'leem' had (8.3.4: leem vervalt).
  Sz1: { soil: 'silt', admixture: 'zandig', intensity: 'zwak' },
  Sz3: { soil: 'silt', admixture: 'zandig', intensity: 'sterk' },
};

// ─── Stap 3: de grinddriehoek (D.2) ─────────────────────────────────────────

type GrindUitkomst = {
  /** Is grind het hoofdbestanddeel? */
  isGrindlaag: boolean;
  /** Bijmenging bij een grindlaag, of de grindigheid als bijmenging bij een andere laag. */
  admixture: string;
  intensity: string;
};

/**
 * NEN 6693: pas boven 50 % is het een grindlaag (8.3.1). Daaronder is grind een bijmenging met
 * de klassen uit tabel D.1: 0-5 zwak, 5-20 matig, 20-50 sterk.
 */
function classifyGrind6693(f: GrainFractions, standard: SoilStandard): GrindUitkomst {
  const { grind: G, zand: Z } = f;
  const fijnDeel = f.lutum + f.silt;

  if (G >= 50) {
    if (fijnDeel > 20) {
      // Het fijne deel bepaalt of de grindlaag kleiig of siltig heet.
      const fine = normaliseFine(f);
      const code = classifyLsz(fine.lutum, fine.silt, fine.zand, standard);
      return { isGrindlaag: true, admixture: code.startsWith('K') ? 'kleiig' : 'siltig', intensity: '' };
    }
    if (Z <= 10) return { isGrindlaag: true, admixture: 'zandig', intensity: 'zwak' };
    if (Z <= 30) return { isGrindlaag: true, admixture: 'zandig', intensity: 'matig' };
    return { isGrindlaag: true, admixture: 'zandig', intensity: 'sterk' };
  }
  if (G >= 20) return { isGrindlaag: false, admixture: 'grindig', intensity: 'sterk' };
  if (G >= 5) return { isGrindlaag: false, admixture: 'grindig', intensity: 'matig' };
  if (G > 0) return { isGrindlaag: false, admixture: 'grindig', intensity: 'zwak' };
  return { isGrindlaag: false, admixture: '', intensity: '' };
}

/**
 * NEN 5104 legt de grens lager: vanaf 30 % grind is het een grindlaag, en de
 * bijmengingsklassen lopen 0-5 zwak, 5-15 matig, 15-30 sterk.
 *
 * ⚠ AFGELEID uit TerraIndex-uitvoer, niet uit de normtekst. De 30 %-grens en de zandigheid van
 * een grindlaag (op het aandeel binnen grind + zand) zijn zo gereconstrueerd.
 */
function classifyGrind5104(f: GrainFractions): GrindUitkomst {
  const { grind: G, zand: Z } = f;
  if (G >= 30) {
    const aandeel = G + Z ? (100 * G) / (G + Z) : 100;
    const intensity = aandeel >= 90 ? 'zwak' : aandeel >= 70 ? 'matig' : aandeel >= 50 ? 'sterk' : 'uiterst';
    return { isGrindlaag: true, admixture: 'zandig', intensity };
  }
  if (G >= 15) return { isGrindlaag: false, admixture: 'grindig', intensity: 'sterk' };
  if (G >= 5) return { isGrindlaag: false, admixture: 'grindig', intensity: 'matig' };
  if (G > 0) return { isGrindlaag: false, admixture: 'grindig', intensity: 'zwak' };
  return { isGrindlaag: false, admixture: '', intensity: '' };
}

// ─── Stap 4: de organische-stofdriehoek (D.3) ───────────────────────────────

/**
 * Een grenslijn in D.3: `a` % organische stof bij lutum 0, oplopend tot `2a` op de rechterzijde
 * van de driehoek (waar silt + zand nul is en lutum dus 100 - 2a).
 */
const osLijn = (a: number, lutumEind: number, lutumTotaal: number): number =>
  a + (a * lutumTotaal) / lutumEind;

export type OrganicResult = {
  /** 'bijmenging' = humeus bovenop de minerale naam; 'grondsoort' = veen wordt het hoofd. */
  kind: 'geen' | 'bijmenging' | 'grondsoort';
  admixture: string;
  intensity: string;
  /** Veen zonder minerale bijmenging (Vm). */
  mineraalarm: boolean;
};

/**
 * Fig. D.3: waar valt deze laag met zijn organische stof?
 *
 * @param osPct   organische stof als % van de TOTALE droge massa
 * @param lutumFijn lutum als % van het grindvrije minerale deel
 *
 * De vijf grenslijnen zijn nagerekend tegen de figuur: op de rechterzijde geldt
 * organische stof + lutum = 100, en 2a valt daar telkens precies op de asmarkering
 * (2,5/95, 8/84, 15/70, 22,5/55, 35/30).
 *
 * ⚠ AANNAME: grind telt in D.3 niet mee -- lutum wordt eerst op het grindvrije deel
 * herschaald. Veen met grind is zeldzaam en de norm laat dit punt open.
 */
export function classifyOrganic(osPct: number | null | undefined, lutumFijn: number): OrganicResult {
  const os = num(osPct);
  if (!os) return { kind: 'geen', admixture: '', intensity: '', mineraalarm: false };

  // Lutum terug naar % van het totaal: de minerale delen vormen samen (100 - os) % daarvan.
  const lutumTotaal = (lutumFijn * (100 - os)) / 100;

  if (os <= osLijn(2.5, 95, lutumTotaal)) return { kind: 'bijmenging', admixture: 'humeus', intensity: 'zwak', mineraalarm: false };
  if (os <= osLijn(8, 84, lutumTotaal)) return { kind: 'bijmenging', admixture: 'humeus', intensity: 'matig', mineraalarm: false };
  if (os <= osLijn(15, 70, lutumTotaal)) return { kind: 'bijmenging', admixture: 'humeus', intensity: 'sterk', mineraalarm: false };

  // Boven de dikke lijn is organische stof het hoofdbestanddeel. Zandig of kleiig volgt de
  // streep-stiplijn van 92 % silt+zand naar de top: lutum = 8 % van het minerale deel.
  const zandig = lutumFijn <= 8;
  if (os <= osLijn(22.5, 55, lutumTotaal)) return { kind: 'grondsoort', admixture: zandig ? 'zandig' : 'kleiig', intensity: 'sterk', mineraalarm: false };
  if (os <= osLijn(35, 30, lutumTotaal)) return { kind: 'grondsoort', admixture: zandig ? 'zandig' : 'kleiig', intensity: 'zwak', mineraalarm: false };
  return { kind: 'grondsoort', admixture: '', intensity: '', mineraalarm: true };
}

// ─── Stap 5: de mediaan uit M50 ─────────────────────────────────────────────

/**
 * De mediaanklassen per norm en per grondsoort, als ondergrens (µm) -> term.
 *
 * NEN 6693 volgt de korrelgroottetabel: zand kent onder elke klasse een onderverdeling, grind
 * niet. De termen dragen hun bereik, zodat een opgeslagen waarde zichzelf uitlegt en de
 * samenvattende termen ('fijn') ernaast kunnen blijven bestaan voor lagen waar niemand een M50
 * van heeft.
 *
 * NEN 5104 kent zes klassen zonder onderverdeling. Vijf van de grenzen vallen samen met die van
 * NEN 6693; alleen 200 tegenover 210 verschilt, en NEN 5104 splitst niet bij 630. Daarom is M50
 * de opgeslagen waarheid en de klasse een vertaling: één getal bedient beide normen.
 */
const MEDIAAN_GRENZEN: Record<SoilStandard, Record<string, ReadonlyArray<readonly [number, string]>>> = {
  nen6693: {
    // Zand: 63 - 2000 µm.
    Z: [[63, 'fijn 63-105'], [105, 'fijn 105-150'], [150, 'fijn 150-200'],
        [200, 'middelgrof 200-300'], [300, 'middelgrof 300-420'], [420, 'middelgrof 420-630'],
        [630, 'grof']],
    // Grind: 2 - 63 mm, hier in µm zodat één veld beide grondsoorten bedient.
    Gr: [[2000, 'fijn'], [6300, 'middelgrof'], [20000, 'grof']],
  },
  nen5104: {
    Z: [[63, 'uiterst fijn'], [105, 'zeer fijn'], [150, 'matig fijn'],
        [210, 'matig grof'], [300, 'zeer grof'], [420, 'uiterst grof']],
    Gr: [[2000, 'fijn'], [6300, 'middelgrof'], [20000, 'grof']],
  },
  // NEN-EN-ISO 14688: dezelfde grenzen als de nationale aanvulling (NA.2) en dezelfde woorden
  // als NEN 6693 -- zo biedt TerraIndex hem aan, en iso14688.ts erft de mediaanlijst van nen6693.
  iso14688: {
    Z: [[63, 'fijn 63-105'], [105, 'fijn 105-150'], [150, 'fijn 150-200'],
        [200, 'middelgrof 200-300'], [300, 'middelgrof 300-420'], [420, 'middelgrof 420-630'],
        [630, 'grof']],
    Gr: [[2000, 'fijn'], [6300, 'middelgrof'], [20000, 'grof']],
  },
};

/** Bovengrens per grondsoort (µm): daarboven is het geen zand respectievelijk geen grind meer. */
const MEDIAAN_BOVENGRENS: Record<string, number> = { Z: 2000, Gr: 63000 };

/**
 * De mediaanklasse bij een M50 in µm. Geeft null als de mediaan hier niet van toepassing is of
 * als de waarde buiten het bereik van deze grondsoort valt -- 180 µm op een grindlaag is geen
 * fijne grind maar een vergissing, en die verzwijgen we liever niet met een klasse.
 */
export function medianClass(
  m50Um: number | null | undefined, soilCode: 'Z' | 'Gr' | string, standard: SoilStandard,
): string | null {
  const m50 = num(m50Um);
  if (m50 == null) return null;
  const grenzen = MEDIAAN_GRENZEN[standard]?.[soilCode];
  if (!grenzen) return null;
  if (m50 < grenzen[0][0] || m50 >= MEDIAAN_BOVENGRENS[soilCode]) return null;
  let term: string | null = null;
  for (const [ondergrens, naam] of grenzen) if (m50 >= ondergrens) term = naam;
  return term;
}

// ─── Het geheel ─────────────────────────────────────────────────────────────

/** Interne afkorting van het hoofdbestanddeel, voor MEDIAAN_GRONDSOORTEN en de mediaantabel. */
const SOIL_CODE: Record<string, string> = { zand: 'Z', grind: 'Gr', klei: 'K', silt: 'S', leem: 'L', veen: 'V' };

/**
 * De hele doorrekening: van percentages naar een grondsoort met zijn bijmengingen.
 *
 * Geeft null als er niets is ingevuld, of als de invoer zichzelf tegenspreekt (dan staat de
 * reden in `notes` van `resolveFractions`; gebruik die apart als je hem wilt tonen).
 */
export function classifyGrainSize(
  input: GrainSizeInput, standard: SoilStandard = 'nen6693',
): GrainClassification | null {
  if (!hasGrainSizeInput(input)) return null;
  const { fractions, notes } = resolveFractions(input);
  if (!fractions) return null;

  const fine = normaliseFine(fractions);
  const grind = usesNen6693Codes(standard)
    ? classifyGrind6693(fractions, standard)
    : classifyGrind5104(fractions);

  let soil: string;
  const admixtures: GrainAdmixture[] = [];

  if (grind.isGrindlaag) {
    soil = 'grind';
    if (grind.admixture) admixtures.push({ admixture: grind.admixture, intensity: grind.intensity });
  } else {
    const code = classifyLsz(fine.lutum, fine.silt, fine.zand, standard);
    const naam = LSZ_NAMEN[code];
    // NEN 6693 kent 'silt' waar NEN 5104 'leem' heeft; de rest van de tabel is gelijk.
    soil = naam.soil === 'silt' && standard === 'nen5104' ? 'leem' : naam.soil;
    admixtures.push({ admixture: naam.admixture, intensity: naam.intensity });
    // Grindigheid komt er als tweede bij te staan. De norm noemt hem in de volledige naam
    // vóór de textuurbijmenging ("zand, matig grindig, sterk siltig"), maar in de opslag --
    // en in TerraIndex -- staat de textuur eerst. Dat is een volgordekwestie in de weergave,
    // geen classificatieverschil.
    if (grind.admixture) admixtures.push({ admixture: grind.admixture, intensity: grind.intensity });
  }

  // Organische stof bovenop: humeus als bijmenging, of veen als hoofdbestanddeel.
  const organic = classifyOrganic(input.organicPct, fine.lutum);
  let mineraalarm = false;
  if (organic.kind === 'grondsoort') {
    soil = 'veen';
    mineraalarm = organic.mineraalarm;
    // De minerale textuur van hiervoor vervalt: bijlage D geeft een organisch hoofdbestanddeel
    // één textuurbijmenging (Vk1/Vz3/...) uit zijn eigen driehoek. De grindigheid blijft wél
    // staan -- die komt uit D.2, een diagram dat los boven de rest ligt, en veen met grind erin
    // bestaat gewoon. Zie maxAdmixtures in nen6693.ts.
    const grindig = admixtures.find((a) => a.admixture === 'grindig');
    admixtures.length = 0;
    if (organic.admixture) admixtures.push({ admixture: organic.admixture, intensity: organic.intensity });
    if (grindig) admixtures.push(grindig);
  } else if (organic.kind === 'bijmenging') {
    admixtures.push({ admixture: organic.admixture, intensity: organic.intensity });
  }

  const soilCode = SOIL_CODE[soil] ?? '';
  return {
    soil,
    admixtures: admixtures.slice(0, 3),
    median: medianClass(input.m50Um, soilCode, standard),
    mineraalarm,
    fractions,
    notes,
  };
}

/** De laagnaam zoals hij in beeld komt: hoofdbestanddeel met zijn bijmengingen erachter. */
export function grainClassificationName(c: GrainClassification | null): string {
  if (!c) return '';
  const hoofd = c.soil.charAt(0).toUpperCase() + c.soil.slice(1);
  const delen = c.admixtures.map((a) => [a.intensity, a.admixture].filter(Boolean).join(' '));
  if (c.mineraalarm) delen.push('mineraalarm');
  return [hoofd, ...delen].join(', ');
}

// ─── Van uitkomst naar laagvelden ───────────────────────────────────────────

/**
 * Versie van de rekenregels hierboven.
 *
 * Gaat mee in de opgeslagen korrelverdeling, zodat later te zien is met welke versie een laag
 * is bepaald. Verschuift er ooit een grens, dan weet je welke lagen daarvoor of daarna zijn
 * uitgerekend, in plaats van te moeten raden of een oude laag nog klopt. Verhogen bij elke
 * wijziging die een uitkomst kan veranderen.
 */
export const GRAIN_SIZE_VERSION = 1;

/** Hoe een waarde in het veld terecht is gekomen. Voor het 'ingesproken'-merkje in de veldapp. */
export type GrainSizeSource = 'voice' | 'manual';

/** De korrelverdeling zoals hij bij een laag wordt bewaard. */
export type LayerGrainSize = GrainSizeInput & {
  /** Per ingevuld veld de herkomst, met de sleutels van GrainSizeInput. */
  source?: Partial<Record<keyof GrainSizeInput, GrainSizeSource>>;
  /** GRAIN_SIZE_VERSION op het moment van opslaan. */
  version?: number;
};

/** De laagvelden die uit een korrelverdeling volgen, met de codes zoals ze worden opgeslagen. */
export type DerivedLayerFields = {
  soilType: string | null;
  mediaan: string | null;
  toevoeging1: string | null; gradatie1: string | null;
  toevoeging2: string | null; gradatie2: string | null;
  toevoeging3: string | null; gradatie3: string | null;
  mineraalarm: boolean;
};

/** Leeg: alles wat de korrelverdeling zou bepalen, op nul. */
const LEGE_VELDEN: DerivedLayerFields = {
  soilType: null, mediaan: null,
  toevoeging1: null, gradatie1: null,
  toevoeging2: null, gradatie2: null,
  toevoeging3: null, gradatie3: null,
  mineraalarm: false,
};

/**
 * De uitkomst omgezet naar de codes die in de laag komen te staan.
 *
 * Geeft null als er geen korrelverdeling is; dan blijft de laag doen wat hij deed en kiest de
 * gebruiker zelf. Is er wél een korrelverdeling, dan zijn deze velden ervan afgeleid en horen
 * ze niet met de hand bewerkt te kunnen worden -- dat is de hele regel: de percentages zijn
 * leidend.
 *
 * De codelijsten verschillen per norm: NEN 6693 kent 'silt' waar NEN 5104 'leem' heeft, en de
 * mediaanklassen zijn andere termen. De classificatie hierboven levert al de juiste termen voor
 * de gevraagde norm; hier worden ze alleen nog opgezocht.
 */
export function grainSizeLayerFields(
  grainSize: LayerGrainSize | null | undefined, standard: SoilStandard = 'nen6693',
): DerivedLayerFields | null {
  const c = grainSize ? classifyGrainSize(grainSize, standard) : null;
  if (!c) return null;

  const is6693 = usesNen6693Codes(standard);
  const soilMap = is6693 ? NEN6693.GRONDSOORT : CODELISTS.BODEMSOORT;
  const admixMap = is6693 ? NEN6693.BIJMENGING : CODELISTS.TOEVOEGING;
  const gradeMap = is6693 ? NEN6693.INTENSITEIT : CODELISTS.GRADATIE;
  const medianMap = is6693 ? NEN6693.MEDIAAN_KLASSE : CODELISTS.MEDIAAN;

  const velden: DerivedLayerFields = {
    ...LEGE_VELDEN,
    soilType: soilMap.get(c.soil) ?? null,
    mediaan: c.median ? (medianMap.get(c.median) ?? null) : null,
    mineraalarm: c.mineraalarm,
  };
  c.admixtures.slice(0, 3).forEach((a, i) => {
    const n = i + 1 as 1 | 2 | 3;
    (velden as Record<string, unknown>)[`toevoeging${n}`] = admixMap.get(a.admixture) ?? null;
    (velden as Record<string, unknown>)[`gradatie${n}`] = a.intensity ? (gradeMap.get(a.intensity) ?? null) : null;
  });
  return velden;
}
